/**
 * reasoningSlice — Jan 2026 SOTA live reasoning chain.
 *
 * The BE emits `reasoning_hop` WS events as the agent works through its
 * pipeline (initial_search → expand_context → ...). The FE accumulates
 * hops per `queryId` so the chat bubble can render Claude-style live
 * activity until the answer lands, then freezes the chain (`isActive=false`).
 */

import type { StateCreator } from "zustand";
import type { LiveReasoning, ReasoningHop } from "./types";
import type { ChatStore } from "./chatStore";

export interface ReasoningSlice {
  liveReasoning: Record<string, LiveReasoning>;

  appendReasoningHop: (queryId: string, hop: ReasoningHop) => void;
  finalizeReasoning: (queryId: string) => void;
  clearReasoning: (queryId: string) => void;
  /**
   * Jul 3, 2026 — append a sanitised thinking delta from the BE
   * ``thinking_delta`` WS event. Idempotent: first call seeds
   * ``streamingThinking = ""`` and stamps ``thinkingStartedAt``; every
   * subsequent call concatenates the delta and updates
   * ``thinkingEndedAt``. Safe to call before any ``reasoning_hop`` has
   * landed — the slice creates the ``LiveReasoning`` shell on the fly.
   */
  appendThinkingDelta: (queryId: string, delta: string) => void;
}

export const createReasoningSlice: StateCreator<
  ChatStore,
  [],
  [],
  ReasoningSlice
> = (set) => ({
  liveReasoning: {},

  appendReasoningHop: (queryId, hop) =>
    set((state) => {
      const existing = state.liveReasoning[queryId] ?? {
        hops: [],
        isActive: true,
      };
      const duplicate = existing.hops.some(
        (known) =>
          known.hop_number === hop.hop_number &&
          known.action === hop.action &&
          known.specialist === hop.specialist &&
          known.user_details === hop.user_details,
      );
      if (duplicate) return state;
      return {
        liveReasoning: {
          ...state.liveReasoning,
          [queryId]: {
            ...existing,
            hops: [...existing.hops, hop],
            isActive: true,
          },
        },
      };
    }),

  finalizeReasoning: (queryId) =>
    set((state) => {
      const existing = state.liveReasoning[queryId];
      if (!existing) return state;
      // Freeze thinkingEndedAt at the last delta timestamp so the
      // "Ragionamento (X.Ys)" chip on the answer bubble reflects the
      // actual thinking window — not the whole turn.
      return {
        liveReasoning: {
          ...state.liveReasoning,
          [queryId]: {
            ...existing,
            isActive: false,
            finalized: true,
            thinkingEndedAt: existing.thinkingEndedAt ?? existing.thinkingStartedAt,
          },
        },
      };
    }),

  clearReasoning: (queryId) =>
    set((state) => {
      const { [queryId]: _, ...rest } = state.liveReasoning;
      return { liveReasoning: rest };
    }),

  appendThinkingDelta: (queryId, delta) =>
    set((state) => {
      if (!delta) return state;
      const now = Date.now();
      const existing = state.liveReasoning[queryId] ?? {
        hops: [],
        isActive: true,
      };
      const nextThinking = (existing.streamingThinking ?? "") + delta;
      return {
        liveReasoning: {
          ...state.liveReasoning,
          [queryId]: {
            ...existing,
            // A thinking delta implies the turn is still active — never
            // downgrade a hop-driven ``isActive: true``.
            isActive: existing.finalized ? existing.isActive : true,
            streamingThinking: nextThinking,
            thinkingStartedAt: existing.thinkingStartedAt ?? now,
            thinkingEndedAt: now,
          },
        },
      };
    }),
});
